import type { StateCreator } from 'zustand';
import { clearTokens } from '@/lib/api-client';
import {
  matchPermission,
  normalizePermissionClaims,
} from '@/features/auth/utils/permission-matcher';
import { setTenantTimezone, clearTenantTimezone } from '@/lib/timezone';
import { setTenantLocaleSettings, clearTenantLocaleSettings } from '@/lib/locale-format';

// Re-export types from the feature auth types for consistency
export type UserRole =
  | 'SUPER_ADMIN'
  | 'TENANT_ADMIN'
  | 'CRM_ADMIN'
  | 'SALES_DIRECTOR'
  | 'HR_MANAGER'
  | 'HR_ADMIN'
  | 'PAYROLL_MANAGER'
  | 'SALES_MANAGER'
  | 'DEPARTMENT_HEAD'
  | 'PEOPLE_MANAGER'
  | 'TEAM_LEAD'
  | 'SALES_REP'
  | 'MANAGER'
  | 'ATTENDANCE_GOVERNANCE_APPROVER'
  | 'EMPLOYEE';
export type Permission = string;

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  role: UserRole;
  permissions: Permission[];
  tenantId?: string;
  tenantName?: string;
  tenantSlug?: string;
  employeeId?: string; // Linked employee record ID (for employees viewing their own profile)
  departmentId?: string;
  departmentName?: string;
  departmentCode?: string;
  positionId?: string;
  positionTitle?: string;
  positionCode?: string;
  isChiefPosition?: boolean;
  teamIds?: string[];
  managedTeamIds?: string[];
  managedDepartmentId?: string;
  avatar?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Tenant {
  id: string;
  name: string;
  code: string;
  slug?: string;
  plan?: string;
  logo?: string;
  timezone?: string;
  locale?: string;
  currency?: string;
  dateFormat?: string;
  timeFormat?: string;
  theme?: {
    primary?: string;
    secondary?: string;
  };
}

export interface AuthSlice {
  // State
  user: User | null;
  tenant: Tenant | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setTenant: (tenant: Tenant | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  login: (user: User, tenant?: Tenant) => void;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  clearError: () => void;

  // Permission checks
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasRole: (role: string | string[]) => boolean;
}

export const createAuthSlice: StateCreator<AuthSlice, [], [], AuthSlice> = (set, get) => ({
  // Initial state
  user: null,
  tenant: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  // Set user
  setUser: (user) => {
    set({
      user: user ? { ...user, permissions: normalizePermissionClaims(user.permissions) } : null,
      isAuthenticated: !!user,
    });
  },

  // Set tenant
  setTenant: (tenant) => {
    set({ tenant });
    if (tenant) {
      localStorage.setItem('tenant_id', tenant.id);
    } else {
      localStorage.removeItem('tenant_id');
    }
  },

  // Set loading state
  setLoading: (loading) => {
    set({ isLoading: loading });
  },

  // Set error
  setError: (error) => {
    set({ error });
  },

  // Login - set user and tenant
  login: (user, tenant) => {
    set({
      user: { ...user, permissions: normalizePermissionClaims(user.permissions) },
      tenant: tenant || null,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    });

    if (tenant) {
      localStorage.setItem('tenant_id', tenant.id);
      if (tenant.timezone) {
        setTenantTimezone(tenant.timezone);
      }
      setTenantLocaleSettings({
        locale: tenant.locale,
        currency: tenant.currency,
        dateFormat: tenant.dateFormat,
        timeFormat: tenant.timeFormat,
      });
    } else {
      localStorage.removeItem('tenant_id');
      clearTenantTimezone();
      clearTenantLocaleSettings();
    }
  },

  // Logout - clear everything
  logout: () => {
    clearTokens();
    localStorage.removeItem('tenant_id');
    clearTenantTimezone();
    clearTenantLocaleSettings();
    set({
      user: null,
      tenant: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  },

  // Update user profile
  updateUser: (updates) => {
    const currentUser = get().user;
    if (currentUser) {
      const nextUser = { ...currentUser, ...updates };
      set({ user: { ...nextUser, permissions: normalizePermissionClaims(nextUser.permissions) } });
    }
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },

  // Permission checks - uses ABAC permission matcher for wildcard support
  hasPermission: (permission) => {
    const user = get().user;
    if (!user) return false;

    // Use ABAC matcher for wildcard-aware permission checking
    return matchPermission(user.permissions || [], permission);
  },

  hasAnyPermission: (permissions) => {
    const user = get().user;
    if (!user) return false;

    // Use ABAC matcher for wildcard-aware permission checking
    return permissions.some((permission) => matchPermission(user.permissions || [], permission));
  },

  hasRole: (role) => {
    const user = get().user;
    if (!user || !user.role) return false;

    // Normalize role for comparison (handle SUPER_ADMIN vs superadmin)
    const normalizeRole = (r: string | undefined): string => {
      if (!r) return '';
      return r.toLowerCase().replace(/_/g, '');
    };
    const userRoleNormalized = normalizeRole(user.role);

    if (Array.isArray(role)) {
      return role.some((r) => normalizeRole(r) === userRoleNormalized);
    }

    return normalizeRole(role) === userRoleNormalized;
  },
});
